import { useCallback, useContext } from 'react';
import { t } from '@lingui/core/macro';
import UWEFormContext from '../context/UWEFormContext';
import { ConflictChoice, OcifXlsxApi, ResolveDataConflictsResult } from '../api/OcifXlsxApi';
import { useToast } from '../components/ui/Toast';
import { buildConflictResolutionPatch } from './conflictResolution';

const NO_KEYS: ReadonlySet<string> = new Set();

/**
 * Resolves calculation conflicts through the backend, then mirrors what the server persisted into
 * the open form — so a later save writes the same values instead of overwriting them with stale ones.
 * Every caller under one form shares a queue and the pending keys (UWEFormContext.conflictResolution):
 * each server save rewrites the report's conflicts whole, so overlapping requests would overwrite each other.
 */
export function useConflictResolver() {
  const uweFormContext = useContext(UWEFormContext);
  const toast = useToast();

  const entityId = uweFormContext?.currentEntity?.id as number | undefined;
  const formDataWriterRef = uweFormContext?.formDataWriterRef;
  const shared = uweFormContext?.conflictResolution;
  const setPendingKeys = shared?.setPendingKeys;
  const queueRef = shared?.queueRef;

  // Never rejects: a failure is reported here and resolves to null, so the shared queue keeps going.
  const request = useCallback(
    async (keys: string[], choice: ConflictChoice): Promise<ResolveDataConflictsResult | null> => {
      try {
        const result = await OcifXlsxApi.resolveConflicts(
          entityId as number,
          keys.map(key => ({ key, choice }))
        );

        const patch = buildConflictResolutionPatch(result);
        if (Object.keys(patch).length > 0) formDataWriterRef?.current?.(patch);

        const failedCount = result.skipped.length;
        if (failedCount > 0) {
          toast.warning(t`${failedCount} conflicts could not be resolved. Reload the report and try again.`);
        }
        return result;
      } catch (e) {
        toast.error((e as Error)?.message || t`Could not resolve the conflicts.`);
        return null;
      }
    },
    [entityId, formDataWriterRef, toast]
  );

  const resolve = useCallback(
    async (keys: string[], choice: ConflictChoice): Promise<ResolveDataConflictsResult | null> => {
      if (!entityId || keys.length === 0 || !setPendingKeys || !queueRef) return null;

      setPendingKeys(prev => new Set([...prev, ...keys]));
      const queued = queueRef.current.then(() => request(keys, choice));
      queueRef.current = queued;
      try {
        return await queued;
      } finally {
        setPendingKeys(prev => {
          const next = new Set(prev);
          keys.forEach(key => next.delete(key));
          return next;
        });
      }
    },
    [entityId, setPendingKeys, queueRef, request]
  );

  return { resolve, pendingKeys: shared?.pendingKeys ?? NO_KEYS };
}
